import { useEffect, useState } from 'react';
import memoize from 'lodash/memoize';
import { metrics } from '@atlassian/browser-metrics';
import { useTenantHasConfluence } from '@atlassian/jira-business-confluence-pages';
import { CARD_COVERS_TYPE, REMOTE_ISSUE_LINKS_TYPE } from '@atlassian/jira-business-constants';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import {
	useExperienceStart,
	useExperienceSuccess,
	useExperienceFail,
} from '@atlassian/jira-business-experience-tracking';
import { isOptimisticIssue } from '@atlassian/jira-business-issue-create';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { ISSUE_LINKS_TYPE } from '@atlassian/jira-platform-field-config';
import { LOAD_EXTENDED_BOARD_DATA_EXPERIENCE } from '../../common/constants';
import { useBoardIssuesOld } from '../../services/board-issues-old';
import { useBoardStore } from '../../services/board-store';
import type { IssuesWithFields, Issue } from '../../services/board-store/types';
import { transformFieldsOld } from '../../utils/board-field-transformer-old';
import { useFieldIds } from '../field-ids-old';
import { useSelectedFields } from '../fields-preference';

const TIME_TO_LOAD_EXTENDED_DATA = 'jwm.jwm-board.load-extended-data';

const emptyArray: Array<number> = [];
// Memoised method to only get ids of issues, details of which havent been retrieved before.
// Array is sorted to pass equality check in memoise.
const getIssuesIdsToFetchDetailsOf = (
	issues: Array<Issue>,
	issuesWithFields: undefined | IssuesWithFields,
	currentFieldIds: Array<string>,
	storedFieldIds: undefined | Array<string>,
) =>
	memoize(() => {
		// Update all issues if current fields ids has a field id that is not in storedFieldIds
		const moreFieldsRequired =
			storedFieldIds != null &&
			!currentFieldIds.every((fieldId) => storedFieldIds.includes(fieldId));

		const toReturn = issues
			.filter((issue) => !isOptimisticIssue(issue.id))
			.filter(
				(issue) =>
					moreFieldsRequired ||
					issuesWithFields == null ||
					issuesWithFields[issue.id] == null ||
					issue.needsExtendedFieldsUpdate === true,
			)
			.map((issue) => issue.id)
			.sort();
		// if the array is empty return the empty array rather then a new reference for the diff check later in useEffect
		if (toReturn.length === 0) {
			return emptyArray;
		}
		return toReturn;
	});

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export const BoardIssueDetailsOld = () => {
	const hasConfluence = useTenantHasConfluence();
	const selectedFields = useSelectedFields();

	const [
		{ issues, issuesWithFields, issueDetailFields },
		{ updateIssuesWithFields, clearStoredFieldIds },
	] = useBoardStore();

	const fieldIds = useFieldIds(false);

	const [loading, setLoading] = useState(false);
	const [hasError, setHasError] = useState(false);

	const projectData = useProject();
	const projectId = String(projectData.id);

	const withConfluenceLinks = hasConfluence
		? Boolean(selectedFields[REMOTE_ISSUE_LINKS_TYPE])
		: false;

	const withCoverMedia = Boolean(selectedFields[CARD_COVERS_TYPE]);

	const withIssueLinks =
		expValEquals('jwm_linked_issues_on_the_board_milestone_1', 'cohort', 'variation') &&
		Boolean(selectedFields[ISSUE_LINKS_TYPE]);

	const { loadIssuesWithFields } = useBoardIssuesOld({
		withConfluenceLinks,
		withIssueLinks,
		withCoverMedia,
	});
	const issueIds = getIssuesIdsToFetchDetailsOf(
		issues,
		issuesWithFields,
		fieldIds,
		issueDetailFields,
	)();

	const startExperience = useExperienceStart(LOAD_EXTENDED_BOARD_DATA_EXPERIENCE);
	const markExperienceSuccess = useExperienceSuccess(LOAD_EXTENDED_BOARD_DATA_EXPERIENCE);
	const markExperienceFailed = useExperienceFail(LOAD_EXTENDED_BOARD_DATA_EXPERIENCE);

	const intl = useIntl();

	// Clear stored field ids on unmount to force reload while keeping the issue details in store
	useEffect(() => clearStoredFieldIds, [clearStoredFieldIds]);

	useEffect(() => {
		if (fieldIds.length > 0 && issueIds.length > 0 && !loading && !hasError) {
			startExperience();
			const timeToLoadMetric = metrics.custom({
				key: TIME_TO_LOAD_EXTENDED_DATA,
			});
			timeToLoadMetric.start();
			setLoading(true);
			loadIssuesWithFields({
				fieldIds,
				issueIds,
				onLoad: (issuesWithDetails) => {
					const transformedIssues = transformFieldsOld(issuesWithDetails, intl);
					updateIssuesWithFields(transformedIssues, fieldIds);
				},
			})
				.then(() => {
					markExperienceSuccess();
				})
				.catch((error) => {
					// Stop all subsequent loads if there's an error
					setHasError(true);
					fireErrorAnalytics({
						meta: {
							id: 'jwmBoardIssueDetails',
							packageName: 'jiraWorkManagementBoard',
							teamName: 'wanjel',
						},
						attributes: {
							message: 'Failed to fetch extended data for issues',
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
					});
					markExperienceFailed('Loading extended data failed', error);
				})
				.finally(() => {
					timeToLoadMetric.stop({
						customData: {
							withCoverMedia,
							withConfluenceLinks:
								hasConfluence && selectedFields[REMOTE_ISSUE_LINKS_TYPE] === true,
							issueCount: issueIds.length,
							fieldCount: fieldIds.length,
							withIssueLinks,
						},
					});
					setLoading(false);
				});
		}
	}, [
		projectId,
		fieldIds,
		issueIds,
		loading,
		hasError,
		loadIssuesWithFields,
		updateIssuesWithFields,
		startExperience,
		markExperienceSuccess,
		markExperienceFailed,
		hasConfluence,
		selectedFields,
		intl,
		withCoverMedia,
		withIssueLinks,
	]);

	return null;
};
